import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { checkoutFlow } from '@commercetools/checkout-browser-sdk';
import toast from 'react-hot-toast';
import { AccountContext } from 'context/account';
import useCountry from 'helpers/hooks/useCountry';
import { useFormat } from 'helpers/hooks/useFormat';
import useLanguage from 'helpers/hooks/useLanguage';
import useSdk from 'helpers/hooks/useSdk';
import { sendGa4BeginCheckoutEvent } from 'helpers/utils/ga4';
import { CF_REGEX } from 'helpers/validation';
import { getCountryFromCode } from 'project.config';
import countryStates from 'static/states.json';
import { useCart, useCheckout, useProjectSettings } from 'frontastic';

const CommercetoolsCheckout = () => {
  const { push: pushRoute } = useRouter();
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const initiatedCheckout = useRef(false);
  const reservationChecked = useRef(false);

  const [isLoading, setIsLoading] = useState(true);

  const { data: projectSettings } = useProjectSettings();

  const sdk = useSdk();

  const [language] = useLanguage();
  const [country] = useCountry();
  const checkoutLocale = language.slice(0, 2);

  const { projectKey } = projectSettings ?? {};

  const { session, isExpired } = useCheckout();

  const { account, logout } = useContext(AccountContext);

  const { data, isEmpty } = useCart();

  const router = useRouter();

  const [reservationsState, setReservationsState] = useState(false);

  useEffect(() => {
    if (reservationChecked.current) return;
    reservationChecked.current = true;
    if (data?.cartId) {
      sdk
        .callAction<{ statusCode: number }>({
          actionName: 'reservations/postReservations',
          payload: { cartId: data.cartId },
        })
        .then((res) => {
          if (!res || res.isError || !res.data?.statusCode || res.data.statusCode < 200 || res.data.statusCode > 299) {
            router.replace('/cart/?error=reservationFailed');
          } else {
            setReservationsState(true);
          }
        });
    }
  }, [data?.cartId, router, sdk]);

  useEffect(() => {
    if (data && isEmpty) {
      router.replace('/cart/?error=emptyCart');
    }
  }, [data, isEmpty, router]);

  useEffect(() => {
    if (!reservationsState || initiatedCheckout.current || !projectKey || !session?.token) {
      return;
    }

    initiatedCheckout.current = true;

    const provinces = countryStates[country.countryCode as keyof typeof countryStates] ?? [];
    const provinceField = {
      label: formatMessage({ id: 'address.province', defaultMessage: 'Provincia' }),
      customType: 'String' as const,
      ...(provinces.length > 0
        ? {
            componentType: 'select' as const,
            valueOptions: provinces.map((x) => ({ value: x.code, label: x.name })),
            validation: {
              required: {
                message: formatMessage({
                  id: 'validation.required',
                  defaultMessage: 'Campo obbligatorio',
                }),
                value: true,
              },
            },
          }
        : {}),
    };
    const fiscalCodeField = country.hasCheckoutCfVat
      ? {
          label: formatMessage({ id: 'address.fiscalCode', defaultMessage: 'Codice fiscale' }),
          customType: 'String' as const,
          validation: {
            required: {
              message: formatMessage({
                id: 'validation.required',
                defaultMessage: 'Campo obbligatorio',
              }),
              value: true,
            },
            pattern: {
              value: CF_REGEX.toString(),
            },
          },
        }
      : undefined;
    const vatIdField = country.hasCheckoutCfVat
      ? {
          label: formatMessage({ id: 'address.vatId', defaultMessage: 'Partita IVA' }),
          customType: 'String' as const,
        }
      : undefined;

    checkoutFlow({
      region: process.env.NEXT_PUBLIC_COMMERCETOOLS_CHECKOUT_REGION || '',
      projectKey,
      sessionId: session.token,
      locale: checkoutLocale,
      styles: {
        '--font-family': "'Inter', sans-serif",
        '--button': '#212121',
        '--radio': '#343434',
        '--checkbox': '#343434',
      },
      languageOverrides: {
        delivery: {
          freeFrom: formatMessage({
            id: 'checkout.freeFrom',
            defaultMessage: 'Gratuita da',
          }),
        },
        shared: {
          optional: ' ', // Avoids showing "(optional)" in the UI
        },
        orderSummary: {
          discountCode: {
            invalid: {
              singular: formatMessage({
                id: 'error.maxUsageReached',
                defaultMessage:
                  'Questo codice sconto non può più essere utilizzato poiché è stato raggiunto il numero massimo di applicazioni. {codes}',
              }),
              plural: formatMessage({
                id: 'error.maxUsageReached',
                defaultMessage:
                  'Questo codice sconto non può più essere utilizzato poiché è stato raggiunto il numero massimo di applicazioni. {codes}',
              }),
            },
          },
        },
      },
      onInfo: (message) => {
        switch (message.code) {
          case 'checkout_started':
            data && sendGa4BeginCheckoutEvent(data);
            break;
          case 'checkout_cancelled':
            pushRoute('/cart');
            break;
          case 'checkout_loaded':
            setIsLoading(false);
            break;
          case 'checkout_completed':
            pushRoute(
              `${process.env.NEXT_PUBLIC_COMMERCETOOLS_CHECKOUT_CALLBACK_URL}?orderId=${
                (message.payload as any)?.order?.id //eslint-disable-line
              }`,
            );
        }
      },
      showBillingSameAsShipping: false,
      forms: {
        default: {
          billingAddress: {
            summaryLayout: [
              ['firstName', 'lastName'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city', 'province', 'country'],
              ['phone'],
              ...((country.hasCheckoutCfVat && [['fiscalCode']]) || []),
              ['company'],
              ...((country.hasCheckoutCfVat && [['vatId']]) || []),
            ],
            custom: {
              type: {
                key: 'addressCustomType',
              },
              fields: {
                province: provinceField,
                ...(fiscalCodeField && vatIdField
                  ? {
                      fiscalCode: fiscalCodeField,
                      vatId: vatIdField,
                    }
                  : {}),
              },
            },
            fields: {
              country: {
                validation: {
                  pattern: data?.country
                    ? {
                        value: `/^${data.country}\$/`,
                        message: formatMessage({
                          id: 'validation.country',
                          defaultMessage: 'La nazione deve essere {country}',
                          values: { country: getCountryFromCode(data.country)?.countryName || '' },
                        }),
                      }
                    : undefined,
                },
              },
            },
            layout: [
              ['firstName', 'lastName'],
              ['country'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city'],
              ['province'],
              ['phone'],
              ...((country.hasCheckoutCfVat && [['fiscalCode']]) || []),
              ['company'],
              ...((country.hasCheckoutCfVat && [['vatId']]) || []),
            ],
          },
          shippingAddress: {
            summaryLayout: [
              ['firstName', 'lastName'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city', 'province', 'country'],
              ['phone'],
            ],
            custom: {
              type: {
                key: 'addressCustomType',
              },
              fields: {
                province: provinceField,
              },
            },
            fields: {
              country: {
                validation: {
                  pattern: data?.country
                    ? {
                        value: `/^${data.country}\$/`,
                        message: formatMessage({
                          id: 'validation.country',
                          defaultMessage: 'La nazione deve essere {country}',
                          values: { country: getCountryFromCode(data.country)?.countryName || '' },
                        }),
                      }
                    : undefined,
                },
              },
            },
            layout: [
              ['firstName', 'lastName'],
              ['email'],
              ['country'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city'],
              ['province'],
              ['phone'],
            ],
          },
        },
      },
    });
  }, [reservationsState, projectKey, country, checkoutLocale, session, pushRoute, formatMessage, data]);

  const errorTriggered = useRef(false);

  useEffect(() => {
    if (account?.accountId && isExpired && !errorTriggered.current) {
      errorTriggered.current = true;
      logout().then(() => {
        pushRoute(`/${locale}/cart`);
        toast.error(formatMessage({ id: 'checkout.tokenExpired' }), { position: 'top-right' });
      });
    }
  }, [formatMessage, isExpired, pushRoute, locale, logout, account]);

  const handleGoingBack = useCallback(() => {
    const checkoutIframe = document.getElementById('ctc-wrapper');

    if (checkoutIframe) checkoutIframe.remove();

    window.onpopstate = null;
  }, []);

  useEffect(() => {
    window.onpopstate = handleGoingBack;
  }, [handleGoingBack]);

  return (
    <div className="min-h-screen lg:bg-neutral-200">
      <div className="relative pt-[6px]">
        {/* eslint-disable-next-line tailwindcss/no-custom-classname*/}
        <div data-ctc className="checkout-Container" />
      </div>
      {isLoading && (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="loading-full-screen" />
        </div>
      )}
    </div>
  );
};

export default CommercetoolsCheckout;
